import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { DrawerContext } from 'context/DrawerContext/DrawerContext';
import { api } from 'common';
import { CitySelect } from 'components';
import { useDispatch, useSelector } from 'react-redux';
import FileUploader from './components/FileUploader';

const CarForm = ({ userId, carId }) => {
  const { handleCloseDrawer } = useContext(DrawerContext);
  const { updateCarData, fetchUsersOnce, getCarData } = api;
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({});
  const cartypes = useSelector(state => state.cartypes.cars);
  const [carTypes, setCarTypes] = useState([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm({
    defaultValues: {
      vehicleMake: '',
      vehicleModel: '',
      vehicleNumber: '',
      other_info: '',
      vehicleReference: '',
      vehicleCity: '',
    },
  });

  // `vehicleCity` no está atado a un <input> nativo: se registra a mano para que
  // la regla `required` se evalúe y `setValue` la actualice desde <CitySelect>.
  useEffect(() => {
    register('vehicleCity', { required: true });
  }, [register]);

  const vehicleCity = watch('vehicleCity');

  // Opciones de tier: nombres de `cartypes` de la MISMA ciudad del vehículo,
  // sin duplicados. Nunca texto libre — el emparejamiento es por match exacto.
  const carTypeOptions = useMemo(() => {
    const city = vehicleCity || formData.vehicleCity;
    const names = (cartypes || [])
      .filter(ct => ct && ct.name && (!city || ct.city === city))
      .map(ct => ct.name);
    // Incluir también los tier(s) ya asignados aunque no coincidan con la ciudad
    // actual (evita warnings de MUI y no borra clasificaciones previas por error).
    return [...new Set([...names, ...(carTypes || [])])];
  }, [cartypes, vehicleCity, formData.vehicleCity, carTypes]);

  const onSubmit = data => {
    try {
      if (!data.vehicleCity) {
        return;
      }
      const newData = {
        ...data,
        id: carId,
        carImgBlob: formData.carImgBlob,
        driver: formData.driver || userId,
        // Clasificación de tier(s). Arreglo vacío ⇒ el vehículo atiende todos
        // los tiers (comportamiento por defecto).
        carTypes: carTypes,
      };
      dispatch(updateCarData(newData));
      dispatch(fetchUsersOnce());
      reset();
      handleCloseDrawer();
    } catch (error) {
      console.error(error);
    }
  };

  const fetchData = async () => {
    try {
      const response = await getCarData(userId);
      const objectEntry = Object.entries(response)[0][0];
      setFormData(response[objectEntry]);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    if (formData && Object.keys(formData).length > 0) {
      reset(formData);
      setCarTypes(
        Array.isArray(formData.carTypes) ? formData.carTypes.filter(Boolean) : [],
      );
    }
  }, [formData, reset]);
  return (
    <Box>
      <Typography
        variant="h5"
        component="h2"
        sx={{ marginBottom: 2, marginTop: 2 }}
      >
        Editar Carro
      </Typography>

      <Divider sx={{ marginBottom: 4 }} />

      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <FileUploader
              label="Subir imagen del carro"
              updateFile={file => {
                setFormData({ ...formData, carImgBlob: file });
              }}
              deleteFile={() => {
                setFormData({ ...formData, carImgBlob: null });
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              {...register('vehicleMake', { required: true })}
              label="Marca"
              defaultValue={formData.vehicleMake}
              fullWidth
              error={errors.vehicleMake}
              helperText={errors.vehicleMake ? 'Este campo es obligatorio' : ''}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              {...register('vehicleModel', { required: true })}
              label="Modelo"
              defaultValue={formData.vehicleModel}
              fullWidth
              error={errors.vehicleModel}
              helperText={
                errors.vehicleModel ? 'Este campo es obligatorio' : ''
              }
              type="number"
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              {...register('vehicleReference', { required: true })}
              label="Linea"
              defaultValue={formData.vehicleReference}
              fullWidth
              error={errors.vehicleReference}
              helperText={
                errors.vehicleReference ? 'Este campo es obligatorio' : ''
              }
              InputLabelProps={{
                shrink:
                  !!formData.vehicleReference || watch('vehicleReference'),
              }}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              {...register('vehicleNumber', { required: true })}
              label="Placa"
              defaultValue={formData.vehicleNumber}
              fullWidth
              error={errors.vehicleNumber}
              helperText={
                errors.vehicleNumber ? 'Este campo es obligatorio' : ''
              }
              InputLabelProps={{
                shrink: !!formData.vehicleNumber || watch('vehicleNumber'),
              }}
            />
          </Grid>

          <Grid item xs={6}>
            <CitySelect
              value={vehicleCity || ''}
              onChange={value =>
                setValue('vehicleCity', value, { shouldValidate: true })
              }
              required
              error={!!errors.vehicleCity}
            />
          </Grid>

          <Grid item xs={12}>
            <Autocomplete
              multiple
              disablePortal
              options={carTypeOptions}
              value={carTypes}
              onChange={(e, value) => setCarTypes(value)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option}
                    {...getTagProps({ index })}
                    key={option}
                  />
                ))
              }
              renderInput={params => (
                <TextField
                  {...params}
                  label="Tipo(s) de servicio"
                  helperText={
                    carTypeOptions.length === 0
                      ? 'No hay tipos de servicio para la ciudad de este vehículo. Créalos en /cartypes.'
                      : 'Vacío = el vehículo atiende todas las categorías.'
                  }
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              {...register('other_info')}
              label="Información adicional"
              defaultValue={formData.other_info}
              fullWidth
              InputLabelProps={{
                shrink: !!formData.other_info || watch('other_info'),
              }}
            />
          </Grid>
        </Grid>

        <Box
          sx={{
            marginTop: 4,
            display: 'flex',
            justifyContent: 'flex-start',
            gap: 2,
          }}
        >
          <Button type="submit" variant="contained" color="primary">
            Actualizar
          </Button>
          <Button onClick={handleCloseDrawer} color="error" variant="outlined">
            Cancelar
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default CarForm;
